/**
 * Lightweight, dependency-free error reporter for the browser apps.
 *
 * Why no hard Sentry dep?
 * - We don't want to force all consumers to install `@sentry/browser` just
 *   to use `@vastra/ui`.
 * - Self-hosted GlitchTip is wire-compatible with Sentry's store endpoint,
 *   so a tiny hand-rolled POST works for both.
 * - If a project later wants full Sentry SDK features (session replay,
 *   tracing), it can call `setErrorReporter()` with its own implementation.
 *
 * Configuration (read once at `initErrorReporter()` call time):
 *   - `REACT_APP_SENTRY_DSN` (webpack-injected): full DSN, e.g.
 *     `https://<key>@o0.ingest.sentry.io/<projectId>`.
 *   - `REACT_APP_SENTRY_ENVIRONMENT` (optional): `prod` | `dev` | ...
 *   - `REACT_APP_SENTRY_RELEASE` (optional): git sha or version tag.
 *
 * When no DSN is set, all reporter calls are silent no-ops — safe to leave
 * in production code paths.
 */

export interface ErrorReporterContext {
  /** Where the error came from, e.g. 'customer-app', 'admin-app'. */
  app?: string;
  /** Extra structured data; keep it small and non-PII. */
  extra?: Record<string, unknown>;
  /** Tags used for filtering/grouping in the dashboard. */
  tags?: Record<string, string>;
  /** React-specific component stack from ErrorBoundary. */
  componentStack?: string;
}

export interface ErrorReporter {
  captureException: (err: unknown, ctx?: ErrorReporterContext) => void;
  captureMessage: (message: string, ctx?: ErrorReporterContext) => void;
}

const noopReporter: ErrorReporter = {
  captureException: () => {},
  captureMessage: () => {},
};

let activeReporter: ErrorReporter = noopReporter;

/**
 * Parse a Sentry DSN into the pieces needed to POST to the ingest endpoint.
 * Returns `null` if the DSN is empty or malformed — caller should fall back
 * to the noop reporter.
 */
function parseDsn(dsn: string): {
  host: string;
  protocol: string;
  projectId: string;
  publicKey: string;
} | null {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!projectId || !url.username) return null;
    return {
      host: url.host,
      protocol: url.protocol.replace(':', ''),
      projectId,
      publicKey: url.username,
    };
  } catch {
    return null;
  }
}

function serializeError(err: unknown): { type: string; value: string; stacktrace?: { frames: { filename: string; function?: string; lineno?: number; colno?: number }[] } } {
  if (err instanceof Error) {
    const frames = parseStack(err.stack || '');
    return {
      type: err.name || 'Error',
      value: err.message || String(err),
      stacktrace: frames.length ? { frames } : undefined,
    };
  }
  return { type: 'NonError', value: safeStringify(err) };
}

function parseStack(stack: string): { filename: string; function?: string; lineno?: number; colno?: number }[] {
  return stack
    .split('\n')
    .slice(1, 50)
    .map((line) => {
      const m = /at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/.exec(line.trim());
      if (!m) return null;
      return {
        function: m[1] || undefined,
        filename: m[2],
        lineno: Number(m[3]),
        colno: Number(m[4]),
      };
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
    .reverse();
}

function safeStringify(v: unknown): string {
  try {
    return typeof v === 'string' ? v : JSON.stringify(v);
  } catch {
    return Object.prototype.toString.call(v);
  }
}

/**
 * Build a Sentry-compatible reporter that sends events via `navigator.sendBeacon`
 * (with a `fetch` fallback). Uses the public store endpoint with `?sentry_key=`
 * — no user secret, safe to ship in a client bundle.
 */
function createSentryReporter(dsn: string, environment?: string, release?: string): ErrorReporter {
  const parsed = parseDsn(dsn);
  if (!parsed) return noopReporter;

  const endpoint = `${parsed.protocol}://${parsed.host}/api/${parsed.projectId}/store/?sentry_version=7&sentry_client=vastra-ui/1.0&sentry_key=${parsed.publicKey}`;

  const send = (payload: Record<string, unknown>): void => {
    try {
      const body = JSON.stringify(payload);
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(endpoint, blob)) return;
      }
      void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Reporter must never throw — swallow all errors.
    }
  };

  const envelope = (level: 'error' | 'info', ctx?: ErrorReporterContext): Record<string, unknown> => ({
    event_id: crypto.randomUUID().replace(/-/g, ''),
    timestamp: Date.now() / 1000,
    level,
    environment,
    release,
    platform: 'javascript',
    sdk: { name: 'vastra-ui', version: '1.0' },
    tags: { app: ctx?.app, ...(ctx?.tags ?? {}) },
    extra: {
      ...(ctx?.extra ?? {}),
      ...(ctx?.componentStack ? { componentStack: ctx.componentStack } : {}),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    },
  });

  return {
    captureException(err, ctx) {
      send({ ...envelope('error', ctx), exception: { values: [serializeError(err)] } });
    },
    captureMessage(message, ctx) {
      send({ ...envelope('info', ctx), message });
    },
  };
}

/**
 * Install the reporter. Call this once at app bootstrap (before rendering)
 * after reading DSN/environment/release from the build-time env.
 *
 * Passing an empty DSN or an invalid DSN installs the noop reporter, which
 * is the safe default for local dev without any telemetry endpoint.
 */
export function initErrorReporter(options: {
  dsn?: string;
  environment?: string;
  release?: string;
}): void {
  const dsn = options.dsn?.trim();
  activeReporter = dsn
    ? createSentryReporter(dsn, options.environment, options.release)
    : noopReporter;
}

/** Swap in a custom reporter (e.g. the full `@sentry/browser` SDK). */
export function setErrorReporter(reporter: ErrorReporter): void {
  activeReporter = reporter;
}

export function captureException(err: unknown, ctx?: ErrorReporterContext): void {
  try {
    activeReporter.captureException(err, ctx);
  } catch {
    // Reporter must never throw into the app.
  }
}

export function captureMessage(message: string, ctx?: ErrorReporterContext): void {
  try {
    activeReporter.captureMessage(message, ctx);
  } catch {
    // Reporter must never throw into the app.
  }
}
